'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { IoHeart } from 'react-icons/io5'
import { LuChevronRight } from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'
import { prefersReducedMotion } from '@/core/lib/motion'
import { cn } from '@/core/utils/common'

import { ChatIcon } from '@/features/veluvit/components/ui/action-icons'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { kfmt } from '@/features/veluvit/lib/format'
import { to } from '@/features/veluvit/lib/route'

import type { CSSProperties } from 'react'

/** 배너 한 장 — 작품 id 와 서버가 정한 배너 문구. 이름 · 지표 · 태그 · 그림은 카탈로그에서 읽는다 */
type BannerSlide = { id: string; line: string }

const AUTO_MS = 4800
/** 넘김 — 앞에서 뒤로 가는 장이 사라지는 시간 · 다시 나타나는 시간 */
const LEAVE_MS = 280
const ENTER_MS = 460
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'
/** 열린 장 폭 · 책등 목표 폭 · 간격(px) — 모바일 / PC */
const SIZE = {
  mobile: { open: 232, spine: 44, gap: 6 },
  desk: { open: 600, spine: 92, gap: 8 },
}
/** 책등 최대 폭 = 접힌 표지 한 장 폭(--cw 와 같은 값) */
const SPINE_MAX = { mobile: 120, desk: 160 }

/**
 * 줄 폭을 재서 장 수 · 칸 자리를 정한다 — 첫 칸은 열린 자리(고정 폭), 나머지는 남는 폭을 나눈 책등 자리. 넓은 화면일수록 책등이 많아진다.
 * 폭은 한 번에 보이는 책등 수만 정한다. 서버가 고른 전체 목록은 순환한다.
 */
function useFit(max: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 0, desk: false })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const desk = window.matchMedia('(min-width: 1001px)')
    const sync = () => setBox({ width: el.clientWidth, desk: desk.matches })
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    desk.addEventListener('change', sync)
    return () => {
      observer.disconnect()
      desk.removeEventListener('change', sync)
    }
  }, [])
  const s = box.desk ? SIZE.desk : SIZE.mobile
  const open = Math.min(s.open, box.width * 0.62)
  const count = box.width
    ? Math.max(
        2,
        Math.min(max, 1 + Math.floor((box.width - open) / (s.spine + s.gap)))
      )
    : Math.min(max, 4)
  const spine =
    count > 1
      ? Math.min(
          box.desk ? SPINE_MAX.desk : SPINE_MAX.mobile,
          (box.width - open - s.gap * (count - 1)) / (count - 1)
        )
      : 0
  /** 칸 자리 — 0 은 열린 자리 */
  const slot = (i: number) =>
    i === 0
      ? { left: 0, width: open }
      : { left: open + s.gap + (i - 1) * (spine + s.gap), width: spine }
  return { ref, count, ready: box.width > 0, open, slot }
}

/** 자동 넘김 — AUTO_MS 마다 다음 장. 올려 둔 동안 · 움직임 줄이기면 서고, 손으로 넘기면 시간을 새로 센다 */
function useAutoIndex(len: number) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  useEffect(() => {
    if (paused || len < 2 || prefersReducedMotion()) return
    const timer = setTimeout(() => setIndex((x) => (x + 1) % len), AUTO_MS)
    return () => clearTimeout(timer)
  }, [index, paused, len])
  const go = useCallback(
    (n: number) => setIndex(((n % len) + len) % len),
    [len]
  )
  return { index, go, setPaused }
}

/**
 * 홈 메인 배너 — 아코디언 포스터(2026-10-07 example 5안 중 ③을 다듬어 채택). 높이 PC 240 · 모바일 180.
 *
 * - 열린 장 = 폭 고정(PC 600 · 모바일 232, 줄의 62% 넘지 않게) — PC 는 표지(2:3) 액자 + 오른쪽 글(지표 · 이름 또는 타이틀 이미지 · 대사 · 태그 칩 · 제작자), 바탕은 표지 색 빛.
 *   모바일은 표지를 장 전체에 깔고(그림이 주인공) 아래 절반 그라데이션 위에 이름 · 대사 한 줄만 — 글자는 카드 제목 규격(16 / 13).
 * - 접힌 장 = 책등, 글 없음. 남는 폭을 나눠 가지며(최대 표지 한 장 폭), 장 수는 줄 폭에 맞춰 자동 — 넓을수록 많이 선다.
 * - 열린 자리는 늘 맨 왼쪽 첫 칸. 접힌 장을 누르면 펼치기만 하고(그 장이 첫 칸으로 오고 앞 장들은 줄 맨 뒤로), 펼친 장을 누르면 작품으로 간다.
 *   열린 장 오른쪽 가장자리 가운데에 다음 장 화살표(동그라미).
 * - 이미지 층은 크기 고정, 글은 폭 고정 — 장 폭이 바뀌어도 이미지가 다시 잘리거나 글 줄이 다시 흐르지 않는다.
 * - 거절 기록: 장면 그림을 넓은 띠에 깔기(몸통만 잘림) · 장면 카드 갤러리 · 대화 버튼 · 포인트색 테두리 · 진행 바 · 호버로 펼치기.
 */
export default function HomeBanner({
  slides,
  className,
}: {
  slides: BannerSlide[]
  className?: string
}) {
  const { catalog, src } = useCatalogMedia()
  const { ref, count, ready, open, slot } = useFit(slides.length)
  const list = slides.filter((s) => catalog.chars[s.id])
  const ids = list.map((s) => s.id)
  const { index, go, setPaused } = useAutoIndex(ids.length)
  const n = ids.length

  /*
   * 넘김 연출 — 칸이 회전할 때 앞에서 뒤로 가는 장(새 칸 번호 > 옛 칸 번호)은 다른 장들 위를 가로질러 날아가지 않는다.
   *   ① 떠나기: 옛 칸에 그대로 선 채 왼쪽으로 살짝 밀리며 사라진다(LEAVE_MS)
   *   ② 숨겨 옮기기: 보이지 않는 채로 새 칸(맨 뒤) 오른쪽에 놓인다 — 이때는 안쪽 전환도 끈다(열림 → 접힘이 순간에 끝나게)
   *   ③ 들어오기: 오른쪽에서 미끄러져 나타난다
   *   나머지 장들은 그동안 한 칸씩 왼쪽으로 당겨져 빈자리를 채운다(left · width 전환).
   * shown = 지금 그려진 순서의 기준 장. index(자동 넘김 · 클릭)가 바뀌면 여기서 한 박자 늦춰 따라간다.
   */
  const [shown, setShown] = useState(0)
  const [leave, setLeave] = useState<Record<string, number>>({})
  const [enter, setEnter] = useState<Record<string, 'from' | 'to'>>({})
  const latest = useRef({ ids, shown })
  // 아래 넘김 효과가 읽을 최신 값 — 렌더 중에 ref 를 쓰지 않고, 넘김 효과보다 먼저 선언해 같은 커밋에서 먼저 돈다
  useEffect(() => {
    latest.current = { ids, shown }
  })
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  useEffect(() => () => timers.current.forEach(clearTimeout), [])
  useEffect(() => {
    const { ids: order, shown: from } = latest.current
    const size = order.length
    if (!size || index === from) return
    timers.current.forEach(clearTimeout)
    timers.current = []
    const placeOf = (k: number, at: number) => (k - at + size) % size
    const wrapped: Record<string, number> = {}
    order.forEach((id, k) => {
      const before = placeOf(k, from)
      if (placeOf(k, index) > before) wrapped[id] = before
    })
    const moving = Object.keys(wrapped)
    setEnter({})
    setLeave(prefersReducedMotion() ? {} : wrapped)
    setShown(index)
    if (prefersReducedMotion() || !moving.length) return
    timers.current.push(
      setTimeout(() => {
        setLeave({})
        setEnter(Object.fromEntries(moving.map((id) => [id, 'from'])))
        // 한 프레임 그려 '오른쪽 · 투명' 자리를 잡은 뒤에 들어오기 시작
        requestAnimationFrame(() =>
          requestAnimationFrame(() =>
            setEnter(Object.fromEntries(moving.map((id) => [id, 'to'])))
          )
        )
        timers.current.push(setTimeout(() => setEnter({}), ENTER_MS + 80))
      }, LEAVE_MS)
    )
  }, [index])

  if (!slides.length) return null

  return (
    <section
      aria-label="추천 작품"
      className={cn('px-4 over10:px-8', className)}
    >
      <style>{`
        @keyframes heroAccOut{to{opacity:0;transform:translateX(-28px)}}
        .home-banner-track{--home-open:min(232px,62%);--home-gap:6px;--home-spine:max(0px,min(120px,calc((100% - var(--home-open) - var(--home-gap) * var(--home-spine-count)) / var(--home-spine-count))))}
        @media (min-width:1001px){.home-banner-track{--home-open:min(600px,62%);--home-gap:8px;--home-spine:max(0px,min(160px,calc((100% - var(--home-open) - var(--home-gap) * var(--home-spine-count)) / var(--home-spine-count))))}}
      `}</style>
      <div
        ref={ref}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        // --cw 접힌 표지 폭(장 높이의 2/3) · --cwo 열린 표지 폭(위아래 여백 뺀 높이의 2/3). 장은 칸 자리(useFit)에 절대 위치로 선다
        style={
          { '--home-spine-count': Math.max(1, count - 1) } as CSSProperties
        }
        className="home-banner-track relative h-45 overflow-x-clip [--cw:120px] [--cwo:104px] over10:h-60 over10:[--cw:160px] over10:[--cwo:133px]"
      >
        {list.map((slide, k) => {
          const { id } = slide
          const c = catalog.chars[id]
          const cover = src(id, 'cover')
          const title = catalog.images[id]?.logo
          // 열린 장은 늘 첫 칸 — 떠나는 중인 장은 옛 칸에 남아 사라진다. 화면 폭이 줄어 장 수가 shown 보다 작아져도 음수가 안 나오게 두 번 나머지
          const leaving = leave[id] !== undefined
          const place = leaving ? leave[id] : (((k - shown) % n) + n) % n
          const on = place === 0
          const phase = enter[id]
          const motion: CSSProperties = leaving
            ? {
                zIndex: 0,
                transition: 'none',
                animation: `heroAccOut ${LEAVE_MS}ms cubic-bezier(0.4,0,1,1) forwards`,
                pointerEvents: 'none',
              }
            : phase === 'from'
              ? {
                  zIndex: 1,
                  transition: 'none',
                  opacity: 0,
                  transform: 'translateX(28px)',
                }
              : phase === 'to'
                ? {
                    zIndex: 1,
                    transition: `opacity ${ENTER_MS}ms ease-out, transform ${ENTER_MS}ms cubic-bezier(0.22,1,0.36,1)`,
                  }
                : { zIndex: on ? 2 : 1 }
          return (
            <Link
              key={id}
              href={to(`/c/${id}`)}
              aria-label={c.name}
              aria-current={on || undefined}
              aria-hidden={place >= count || undefined}
              inert={place >= count}
              style={
                {
                  ...(ready ? slot(place) : { '--home-slot': place - 1 }),
                  ...motion,
                } as CSSProperties
              }
              // 키보드(Tab)로 들어왔을 때만 펼친다 — 마우스로 누를 때 포커스가 먼저 펼쳐 버리면 이어지는 클릭이 작품으로 넘어가 버린다
              onFocus={(e) => {
                if (e.currentTarget.matches(':focus-visible')) go(k)
              }}
              // 접힌 장을 누르면 펼치기만 하고(작품으로 가지 않는다), 펼친 장을 누르면 작품으로 간다
              onClick={(e) => {
                if (!on) {
                  e.preventDefault()
                  go(k)
                }
              }}
              className={cn(
                'group absolute top-0 h-full overflow-hidden rounded-[18px] bg-raised no-underline transition-[left,width] duration-[650ms]',
                on
                  ? 'left-0 w-(--home-open)'
                  : 'left-[calc(var(--home-open)+var(--home-gap)+var(--home-slot)*(var(--home-spine)+var(--home-gap)))] w-(--home-spine)',
                EASE,
                // 숨겨 옮기는 한 프레임 — 안쪽(표지 · 글) 전환도 꺼서 열린 모양이 접힌 모양으로 순간에 바뀌게
                phase === 'from' && '[&_*]:transition-none!'
              )}
            >
              {/* 열린 장 바탕 — 표지 색 빛 */}
              <span
                aria-hidden="true"
                className={cn(
                  'absolute inset-0 transition-opacity duration-500',
                  on ? 'opacity-100' : 'opacity-0'
                )}
              >
                <SafeImage
                  src={cover}
                  alt=""
                  fill
                  sizes="160px"
                  quality={30}
                  className="scale-150 object-cover blur-2xl saturate-150"
                  appearance="plain"
                />
                <span className="absolute inset-0 bg-black/45" />
              </span>

              {/* 표지 — 접힌 장은 가운데 정렬로 띠만. 열리면 PC 는 왼쪽에 둥근 액자, 모바일은 장 전체에 깐다
                  (모바일 장이 좁아 액자 옆에 글 자리가 없다 — 액자 위에 글이 반쯤 겹쳐 지저분했다, 2026-10-15) */}
              <span
                className={cn(
                  'absolute overflow-hidden transition-[translate,top,bottom,left,width,border-radius,box-shadow,filter] duration-[650ms]',
                  EASE,
                  on
                    ? 'inset-y-0 left-0 w-full translate-x-0 rounded-[18px] brightness-100 over10:inset-y-5 over10:left-5 over10:w-(--cwo) over10:rounded-[12px] over10:shadow-[0_16px_30px_-10px_rgb(0_0_0/0.9),0_0_0_1px_rgb(255_255_255/0.16)]'
                    : 'inset-y-0 left-1/2 w-(--cw) -translate-x-1/2 rounded-[18px] brightness-[0.62] group-hover:brightness-90'
                )}
              >
                {/* 책등 가운데 세로 반사광은 "이상한 가운데 줄"로 보여 뺐다(2026-10-07) */}
                <SafeImage
                  src={cover}
                  alt=""
                  fill
                  sizes="(min-width: 1001px) 160px, 240px"
                  preload={k < 4}
                  className="object-cover object-[50%_20%]"
                  appearance="plain"
                />
                <WorkTitleOverlay
                  src={title}
                  sizes="240px"
                  className={
                    on
                      ? '[--work-title-bottom:45%] over10:[--work-title-bottom:16%]'
                      : undefined
                  }
                />
              </span>

              {/* 모바일 — 글이 표지 위 아래쪽에 얹히므로 글 자리(아래 절반)만 누른다. 그림이 주인공이라 위는 그대로 둔다 */}
              <span
                aria-hidden="true"
                className={cn(
                  'absolute inset-x-0 bottom-0 h-1/2 bg-[linear-gradient(0deg,rgb(0_0_0/0.8),rgb(0_0_0/0.35)_60%,transparent)] transition-opacity duration-500 over10:hidden',
                  on ? 'opacity-100' : 'opacity-0'
                )}
              />

              {/* 글 — 열린 장에만. 폭 고정이라 열리는 동안 줄바꿈이 바뀌지 않고, 장이 반쯤 열린 뒤 미끄러져 들어온다(닫힐 땐 바로 사라짐).
                  모바일은 표지 위 아래쪽, PC 는 표지 오른쪽 세로 가운데. */}
              <span
                className={cn(
                  'absolute right-3 bottom-3 left-3 flex flex-col justify-end transition-[opacity,translate] over10:top-5 over10:right-auto over10:bottom-5 over10:left-[calc(var(--cwo)+52px)] over10:w-[min(360px,21vw)] over10:justify-center',
                  EASE,
                  on
                    ? 'translate-x-0 opacity-100 delay-[220ms] duration-500'
                    : 'pointer-events-none -translate-x-3 opacity-0 delay-0 duration-150'
                )}
              >
                {/* 지표 — PC 만. 모바일 배너는 좁아 제목 · 대사 · 태그만 남긴다(지표는 아래 목록 카드에 있다) */}
                <span className="hidden items-center gap-3 text-c13 font-semibold text-white/75 tabular-nums over10:flex">
                  <span className="inline-flex items-center gap-1">
                    <IoHeart aria-hidden="true" className="size-3.5" />
                    {kfmt(c.likes)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <ChatIcon aria-hidden="true" className="size-3.5" />
                    {kfmt(c.chats)}
                  </span>
                </span>
                <b className="line-clamp-2 text-c16 leading-[1.4] font-bold break-keep text-white drop-shadow-[0_1px_6px_rgb(0_0_0/0.6)] over10:mt-2 over10:text-c22 over10:leading-[1.25] over10:font-black over10:tracking-[-0.02em]">
                  {c.name}
                </b>
                <span className="mt-0.5 line-clamp-1 text-c13 leading-[1.5] text-white/75 over10:mt-2.5 over10:line-clamp-2 over10:text-c14">
                  {slide.line}
                </span>
                {/* 태그 칩 — PC 만. 모바일 배너는 그림을 덜 가리게 제목 · 대사 한 줄만 */}
                <span className="mt-4 hidden h-6 flex-wrap gap-1 overflow-hidden over10:flex">
                  {c.tags.slice(0, 4).map((t) => (
                    <span
                      key={t}
                      className="inline-flex h-5.5 items-center rounded-full bg-white/15 px-2 text-c11 font-medium text-white backdrop-blur-md over10:h-6 over10:px-2.5 over10:text-c12"
                    >
                      #{t}
                    </span>
                  ))}
                </span>
                <span className="mt-2 hidden truncate text-c12 text-white/50 over10:mt-3 over10:block over10:text-c13">
                  {c.author}
                </span>
              </span>
            </Link>
          )
        })}

        {/* 다음 장 화살표 — 열린 장 오른쪽 가장자리 세로 가운데에 걸친 동그라미 */}
        {n > 1 && (
          <Button
            type="button"
            aria-label="다음 작품"
            onClick={() => go(index + 1)}
            style={ready ? { left: open } : undefined}
            className="absolute top-1/2 left-(--home-open) z-[3] grid size-9 -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-black/75 text-white shadow-[0_6px_16px_-4px_rgb(0_0_0/0.7),inset_0_0_0_1px_rgb(255_255_255/0.22)] backdrop-blur-md transition-[scale,background-color] duration-200 hover:scale-110 hover:bg-black/90 over10:size-10"
            variant="bare"
          >
            <LuChevronRight aria-hidden="true" className="size-5" />
          </Button>
        )}
      </div>
    </section>
  )
}
